/**
 * Software-keyboard dismissal detector, fed with visualViewport heights.
 *
 * Mobile focus modes (product-page MobileFocusModal, DS Input focus sheet)
 * must exit when the customer collapses the keyboard without blurring the
 * field (iOS swipe-down, Android back button). The only signal is the visual
 * viewport growing back.
 *
 * The old check compared each resize event with the previous one and
 * required a single jump > 100px. iOS fires ONE resize when the keyboard
 * goes, so that worked; Chrome on Android animates the keyboard and fires
 * several small resizes, so no single step crossed the threshold and focus
 * mode never exited. This detector instead remembers the tallest
 * (keyboard-closed) height it has seen: the keyboard counts as open once the
 * viewport is more than KEYBOARD_MIN_DELTA below it, and as dismissed on the
 * first event that brings the viewport back within that margin — however
 * many steps the animation takes. Address-bar show/hide (< 100px) never
 * qualifies, and a taller viewport seen later just raises the baseline.
 *
 * Returns a function `(viewportHeight) => boolean` — true exactly once per
 * open→closed cycle, on the dismissing event.
 */
export const KEYBOARD_MIN_DELTA = 100;

export function createKeyboardDismissDetector(initialHeight) {
  let baseline = Number.isFinite(initialHeight) ? initialHeight : 0;
  let keyboardOpen = false;
  return (viewportHeight) => {
    const vh = Number(viewportHeight);
    if (!Number.isFinite(vh)) return false;
    if (vh > baseline) baseline = vh;
    if (vh < baseline - KEYBOARD_MIN_DELTA) {
      keyboardOpen = true;
      return false;
    }
    if (keyboardOpen) {
      keyboardOpen = false;
      return true;
    }
    return false;
  };
}

export default createKeyboardDismissDetector;
